import { Button } from "react-bootstrap";
import "./my-resume.scss";
import ADD_IMG from "../../../assets/images/add.png";
import config from "../../../config";
import { useContext, useEffect, useState, useCallback, useMemo, memo } from "react";
import { AllResumeModel } from "../../../model/all-resume.model";
import { GetResumeIdModel } from "../../../model/get-resume.model";
import { Alert, CircularProgress, IconButton, Snackbar, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import { SlotResumeModel } from "../../../model/resume-builder/slot-resume.model";
import LoadingModal from "../../../modal/loading/loading";
import MenuComponent from "../../menu/menu";
import navigateConfig from "../../../navigate.config";
import { ProvideContext } from "../../../context/provider.context";
import { TranslateModel } from "../../../model/translate.model";
import ResumeBuilderInstantService from "../../../services/resume-builder/resume-builder-instance.service";
import { useNavigate } from "react-router-dom";
import UploadResumeModal from "../../../modal/jobs/upload-resume/upload-resume.modal";
import { useProfile } from "../../../context/profile.context";
import { getOneResumeModel } from "../../../model/resume-builder/get-one-resume.model";
import RemoveReseumeModal from "../../../modal/remove-resume";
import BG_CIRCLE_MAIN from "../../../assets/images/circle-bg-main.svg";
import UtilityService from "../../../utility/utility.service";
const MyResumeComponent = () => {
    const { translateGlobal }: any = useContext(ProvideContext);
    const [translate] = useState<TranslateModel>(translateGlobal);
    const [allResume, setAllResume] = useState<AllResumeModel>();
    const [slotModel, setSlotModel] = useState<SlotResumeModel>();
    const [isUploadResume, setIsUploadResume] = useState(false);
    const [isModalSnack, setIsModalSnack] = useState(false);
    const navigate = useNavigate();
    const [profile, error, isLoadingProfile] = useProfile();
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmDelete, setIsConfirmDelete] = useState(false);
    const [isResumeFull, setIsResumeFull] = useState(false);
    const [resumeDelete, setResumeDelete] = useState<getOneResumeModel>();

    // Memoized functions
    const getAllResumes = useCallback(() => {
        setIsLoading(true);
        ResumeBuilderInstantService.getAllResume()
            .then((res) => {
                if (res.data) {
                    const result = res.data as AllResumeModel;
                    if (result.resumes) {
                        const allResumeMod = result.resumes.map((item: GetResumeIdModel) => ({
                            ...item,
                            is_open: false
                        }));
                        setAllResume({ ...result, resumes: allResumeMod });
                    }
                }
            })
            .catch((error) => {
                console.error('Error fetching resumes:', error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const checkSlot = useCallback(() => {
        ResumeBuilderInstantService.slotResume()
            .then((res) => {
                if (res.data) {
                    const result = res.data as SlotResumeModel;
                    setSlotModel(result);
                    setIsResumeFull(false);
                }
            })
            .catch((errors) => {
                console.error('Error checking slot:', errors);
                if (errors.response?.data?.error === "Cannot create a new resume") {
                    setIsResumeFull(true);
                }
            });
    }, []);

    // Event handlers with useCallback
    const clickAddNew = useCallback(() => {
        if (slotModel?.can_add_resume) {
            navigate(navigateConfig.resume.choose_template);
        }
    }, [slotModel, navigate]);

    const clickUpload = useCallback(() => {
        setIsUploadResume(true);
    }, []);

    const handleUploadResume = useCallback(() => {
        setIsUploadResume(prev => !prev);
    }, []);

    const clickView = useCallback((resume_uuid: string) => {
        window.open(config.url + 'resume/view/' + resume_uuid, "_blank");
    }, []);

    const clickEdit = useCallback((resume_uuid: string) => {
        navigate(navigateConfig.resume.builder + resume_uuid);
    }, [navigate]);

    const clickDelete = useCallback((resume: GetResumeIdModel) => {
        setResumeDelete(resume as getOneResumeModel);
        setIsConfirmDelete(true);
    }, []);

    const handleCloseConfirmDelete = useCallback(() => {
        setIsConfirmDelete(false);
        setResumeDelete(undefined);
    }, []);

    const confirmDelete = useCallback(() => {
        if (resumeDelete?.resume_uuid) {
            setIsLoading(true);
            ResumeBuilderInstantService.deleteResume(resumeDelete.resume_uuid)
                .then((res) => {
                    if (res.status === 200) {
                        getAllResumes();
                        checkSlot();
                        handleCloseConfirmDelete();
                    }
                })
                .catch((error) => {
                    console.error('Error deleting resume:', error);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [resumeDelete, getAllResumes, checkSlot, handleCloseConfirmDelete]);

    const onSelectChange = useCallback((checked: boolean, resume_uuid: string, resume_status: string) => {
        if (resume_status === "PUBLISHED") {
            ResumeBuilderInstantService.updateResumeDefault(resume_uuid)
                .then((res) => {
                    if (res.data) {
                        getAllResumes();
                    }
                })
                .catch((error) => {
                    console.error('Error updating resume default:', error);
                });
        } else {
            setIsModalSnack(true);
        }
    }, [getAllResumes]);

    const handleCloseSnack = useCallback(() => {
        setIsModalSnack(false);
    }, []);

    const setKey = useCallback(() => {
        getAllResumes();
    }, [getAllResumes]);

    const toggleResumeOpen = useCallback((index: number) => {
        setAllResume(prev => {
            if (!prev?.resumes) return prev;
            const updatedResumes = [...prev.resumes];
            updatedResumes[index] = {
                ...updatedResumes[index],
                is_open: !updatedResumes[index].is_open
            };
            return { ...prev, resumes: updatedResumes };
        });
    }, []);

    // Effects
    useEffect(() => {
        if (profile) {
            UtilityService().clickSendEvent("my_resumes_page", "load");
            getAllResumes();
        }
    }, [profile, getAllResumes]);

    useEffect(() => {
        if (!slotModel && !isResumeFull) {
            checkSlot();
        }
    }, [slotModel, isResumeFull, checkSlot]);


    // Memoized Row component
    const Row = memo(({ resume, index, translate, onToggleOpen, onView, onDelete, onSelectChange, onEdit }: {
        resume: GetResumeIdModel;
        index: number;
        translate: TranslateModel;
        onToggleOpen: (index: number) => void;
        onView: (uuid: string) => void;
        onDelete: (resume: GetResumeIdModel) => void;
        onSelectChange: (checked: boolean, uuid: string, status: string) => void;
        onEdit: (uuid: string) => void;
    }) => {
        const handleToggle = useCallback(() => {
            onToggleOpen(index);
        }, [index, onToggleOpen]);

        const handleView = useCallback(() => {
            if (resume.resume_uuid) {
                onView(resume.resume_uuid);
            }
        }, [resume.resume_uuid, onView]);

        const handleDelete = useCallback(() => {
            onDelete(resume);
        }, [resume, onDelete]);

        const handleEdit = useCallback(() => {
            if (resume.resume_uuid) {
                onEdit(resume.resume_uuid);
            }
        }, [resume.resume_uuid, onEdit]);

        const handleSwitchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
            if (resume.resume_uuid && resume.resume_status) {
                onSelectChange(e.target.checked, resume.resume_uuid, resume.resume_status);
            }
        }, [resume.resume_uuid, resume.resume_status, onSelectChange]);

        return (
            <>
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                    <TableCell align="center" width={'50px'} className="my-resume-item-cell-text d-md-table-cell d-none">
                        {index + 1}
                    </TableCell>
                    <TableCell align="left" className="my-resume-item-cell-text">
                        {resume.resume_name}
                    </TableCell>
                    <TableCell align="left" className="my-resume-item-cell-text d-md-table-cell d-none">
                        <div className="my-resume-item-status-control">
                            {resume.resume_status === "PUBLISHED" 
                                ? translate.resume_builder.resume_published 
                                : translate.resume_builder.resume_draft}
                        </div>
                    </TableCell>
                    <TableCell align="left" className="my-resume-item-cell-text d-md-table-cell d-none">
                        {resume.created_at && new Date(resume.created_at).toDateString()}
                    </TableCell>
                    <TableCell align="left" className="my-resume-item-cell-text">
                        <Switch 
                            checked={resume.is_default || false}
                            onChange={handleSwitchChange}
                            inputProps={{ 'aria-label': 'Set as default resume' }} 
                        />
                    </TableCell>
                    <TableCell align="left" className="d-md-table-cell d-none">
                        <div className="my-resume-item-button-control">
                            <Button 
                                className="my-resume-item-share-view-button" 
                                onClick={handleView}
                            >
                                {translate.resume_builder.view_resume}
                            </Button>
                            <Button 
                                className="my-resume-item-share-view-button" 
                                onClick={handleEdit}
                            >
                                {translate.other.edit || "แก้ไข"}
                            </Button>
                            <Button 
                                className="my-resume-item-delete-button" 
                                onClick={handleDelete}
                            >
                                {translate.resume_builder.delete_resume}
                            </Button>
                        </div>
                    </TableCell>
                    <TableCell align="left" className="my-resume-item-cell-text d-table-cell d-md-none">
                        <IconButton
                            className="my-resume-collapse-size"
                            aria-label="expand row"
                            size="small"
                            onClick={handleToggle}
                        >
                            {resume.is_open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                        <Collapse in={resume.is_open} timeout="auto" className="d-block d-md-none" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                                <Table>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell style={{ border: 'none' }}>
                                                <div className="my-resume-item-button-control">
                                                    <Button 
                                                        className="my-resume-item-share-view-button" 
                                                        onClick={handleView}
                                                    >
                                                        {translate.resume_builder.view_resume}
                                                    </Button>
                                                    <Button 
                                                        className="my-resume-item-share-view-button" 
                                                        onClick={handleEdit}
                                                    >
                                                        {translate.other.edit || "แก้ไข"}
                                                    </Button>
                                                    <Button 
                                                        className="my-resume-item-delete-button" 
                                                        onClick={handleDelete}
                                                    >
                                                        {translate.resume_builder.delete_resume}
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </>
        );
    });
    return (

        <div className="main-control">
            <MenuComponent></MenuComponent>
            <div className="my-resume">
                <img src={BG_CIRCLE_MAIN} className="bg-circle-main"></img>
                <div className="my-resume-control">
                    <div className="my-resume-header-control">
                        <h1 className="my-resume-header">{translate.resume_builder.my_resume}</h1>
                        <div className="my-resume-add-button-control">
                            {
                                slotModel ?
                                    <>
                                        <span className="my-resume-add-button-text">{translate.resume_builder.total_resume}</span>
                                        <span className="my-resume-add-button-text-bold">{slotModel.used_slot}/{slotModel.total_slot}</span>
                                    </> :
                                    isResumeFull &&
                                    <>
                                        <span className="my-resume-add-button-text">{translate.resume_builder.total_resume}</span>
                                        <span className="my-resume-add-button-text-bold">{10}/{10}</span>
                                    </>


                            }
                            {
                                !isResumeFull &&

                                <>
                                    <Button className="my-resume-add-button" onClick={clickAddNew}><img src={ADD_IMG} className="my-resume-add-img"></img>{translate.resume_builder.add_resume}</Button>
                                    <span className="my-resume-import-or">{translate.other.or}</span>
                                    <span className="my-resume-import-resume" onClick={clickUpload}>{translate.resume_builder.import_resume}</span>
                                </>
                            }

                        </div>

                    </div>
                    {
                        isLoadingProfile ? (
                            <div className="my-resume-loading-control">
                                <div className="my-resume-loading-flex">
                                    <CircularProgress className="my-resume-loading"></CircularProgress>
                                    <span className="my-resume-loading-text">{translate.other.loading}</span>
                                </div>
                            </div>
                        ) : isLoading && (!allResume || allResume.resumes.length === 0) ? (
                            <div className="my-resume-loading-control">
                                <div className="my-resume-loading-flex">
                                    <CircularProgress className="my-resume-loading"></CircularProgress>
                                    <span className="my-resume-loading-text">{translate.other.loading}</span>
                                </div>
                            </div>
                        ) : allResume && allResume.resumes.length > 0 ? (
                            <div className="my-resume-list-control-desktop" style={{ position: 'relative' }}>
                                {isLoading && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        zIndex: 10,
                                        borderRadius: '8px'
                                    }}>
                                        <div className="my-resume-loading-flex">
                                            <CircularProgress className="my-resume-loading"></CircularProgress>
                                            <span className="my-resume-loading-text" style={{ marginLeft: '12px' }}>{translate.other.loading}</span>
                                        </div>
                                    </div>
                                )}
                                <TableContainer>
                                    <Table className="my-resume-table-size" aria-label="simple table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="center" style={{ width: '10%' }} className="d-md-table-cell d-none ">{translate.resume_builder.resume_order}</TableCell>
                                                <TableCell align="left" style={{ width: '30%' }}>{translate.resume_builder.resume_name}</TableCell>
                                                <TableCell align="left" style={{ width: '10%' }} >{translate.resume_builder.resume_status}</TableCell>
                                                <TableCell align="left" style={{ width: '10%' }} className="d-md-table-cell d-none ">{translate.resume_builder.create_at}</TableCell>
                                                <TableCell align="left" style={{ width: '10%' }} className="d-md-table-cell d-none ">{translate.resume_builder.resume_default}</TableCell>
                                                <TableCell align="left" style={{ width: '10%' }}></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {allResume.resumes.map((resume: GetResumeIdModel, index: number) => (
                                                <Row 
                                                    key={resume.resume_uuid} 
                                                    resume={resume} 
                                                    index={index}
                                                    translate={translate}
                                                    onToggleOpen={toggleResumeOpen}
                                                    onView={clickView}
                                                    onDelete={clickDelete}
                                                    onSelectChange={onSelectChange}
                                                    onEdit={clickEdit}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        ) : allResume && allResume?.resumes.length === 0 && !isLoading && (
                            <div className="no-resume-control">
                                <div className="no-resume-text-control">
                                    <span className="no-resume-text">{translate.error.no_resume}</span>
                                    <Button className="no-resume-add-button" onClick={clickAddNew}><img src={ADD_IMG} className="my-resume-add-img"></img>{translate.other.add}</Button>
                                </div>
                            </div>
                        )
                    }

                </div>
            </div>
            {isLoading && <LoadingModal open={isLoading} onclose={setIsLoading} />}
            {
                isUploadResume && <UploadResumeModal open={isUploadResume} handleClose={handleUploadResume} setKey={setKey} pathName="resume_builder"> </UploadResumeModal>
            }

            <Snackbar open={isModalSnack} autoHideDuration={3000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={handleCloseSnack} >
                <Alert onClose={handleCloseSnack} severity="error" sx={{ width: '100%' }}>
                    {translate.error.resume_change_default}
                </Alert>
            </Snackbar>
            {
                isConfirmDelete &&
                <RemoveReseumeModal open={isConfirmDelete} resume={resumeDelete} confirmDelete={confirmDelete} handleClose={handleCloseConfirmDelete}></RemoveReseumeModal>
            }
        </div>
    )
}

export default MyResumeComponent;